import {startRegistration, startAuthentication, browserSupportsWebAuthn, browserSupportsWebAuthnAutofill, platformAuthenticatorIsAvailable} from "@simplewebauthn/browser";
import {readTokens} from "./storage";

// SPA-side WebAuthn helpers. Wraps @simplewebauthn/browser + the auth-service
// /login/passkey/* endpoints.

const DISMISS_KEY = (userId: number) => `passkey_setup_dismissed_${userId}`;

/** Should we show the "Set up Touch ID" interstitial after a successful login? */
export async function shouldOfferPasskeySetup(userId: number): Promise<boolean> {
    if (!browserSupportsWebAuthn()) return false;
    if (!(await platformAuthenticatorIsAvailable())) return false;
    if (localStorage.getItem(DISMISS_KEY(userId)) === "true") return false;
    return true;
}

export function dismissPasskeySetup(userId: number) {
    localStorage.setItem(DISMISS_KEY(userId), "true");
}

/** Enroll a new passkey for the currently-authenticated user. Throws on cancel/error. */
export async function enrollPasskey(): Promise<void> {
    const tokens = readTokens();
    if (!tokens) throw new Error("not_authenticated");

    const optsRes = await fetch("/login/passkey/register/options", {
        method: "POST",
        headers: {"Content-Type": "application/json", Authorization: `Bearer ${tokens.accessToken}`},
        body: "{}",
    });
    if (!optsRes.ok) throw new Error("options_failed");
    const options = await optsRes.json();

    const attResp = await startRegistration({optionsJSON: options});

    const verifyRes = await fetch("/login/passkey/register/verify", {
        method: "POST",
        headers: {"Content-Type": "application/json", Authorization: `Bearer ${tokens.accessToken}`},
        body: JSON.stringify(attResp),
    });
    if (!verifyRes.ok) throw new Error("verify_failed");
}

export interface PasskeySignInResult {accessToken: string; refreshToken: string; userId: number}

/** Sign in with a passkey. By default uses an explicit modal prompt
 *  (button click). Pass `useBrowserAutofill: true` to start the
 *  Conditional Mediation flow that surfaces passkeys silently inside
 *  the browser's autofill dropdown on the email field — no UI of our
 *  own appears unless the user picks one.
 *
 *  Conditional flow MUST be started early (e.g. on form mount) and the
 *  promise stays pending until the user picks a credential or navigates
 *  away. Cancelling = caller should ignore the rejection.
 */
export async function signInWithPasskey(opts: {useBrowserAutofill?: boolean} = {}): Promise<PasskeySignInResult | null> {
    if (!browserSupportsWebAuthn()) return null;
    if (opts.useBrowserAutofill && !(await browserSupportsWebAuthnAutofill())) return null;

    const optsRes = await fetch("/login/passkey/auth/options", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: "{}",
    });
    if (!optsRes.ok) return null;
    const options = await optsRes.json();

    const assertion = await startAuthentication({
        optionsJSON: options,
        useBrowserAutofill: opts.useBrowserAutofill,
    });

    const verifyRes = await fetch("/login/passkey/auth/verify", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(assertion),
    });
    if (!verifyRes.ok) return null;
    const r = await verifyRes.json();
    return {accessToken: r.access_token, refreshToken: r.refresh_token, userId: r.user_id};
}

/** Pull the user_id out of an access-token JWT (sub claim). Used by the
 * magic-link landing path where we get tokens but no separate user_id. */
export function userIdFromAccessToken(accessToken: string): number | null {
    try {
        const payload = accessToken.split(".")[1];
        const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
        const sub = Number(json.sub);
        return Number.isFinite(sub) ? sub : null;
    } catch {
        return null;
    }
}
